import { Search as SearchIcon } from "baseui/icon/icon-exports";
import classNames from "classnames";
import React, { useEffect, useState } from "react";

import { FitChips } from "../../../components/newUI/chips";
import { ContactTagsModel } from "../../../factories/contacts/models/contact-tag.model";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useAddCustomerTagsMutation,
  useDeleteCustomerTagMutation,
  useSearchTagsQuery,
} from "../../../slices/ContactTagsSlice";

type Props = {
  tags: ContactTagsModel[];
  onClose: () => void;
  customerId: string;
};

const AddTagsModal: React.FC<Props> = ({ tags, onClose, customerId }) => {
  const [isError, setIsError] = useState(false);
  const [tagsValue, setTagsValue] = useState(tags?.map((i) => i.tagId) || []);
  const [tagValue, setTagValue] = useState("");

  const { data: allTags, isLoading } = useSearchTagsQuery({
    orgId: getLinkedFirm()?.orgId || "",
    query: tagValue || null,
  });
  const [addTags] = useAddCustomerTagsMutation();
  const [replaceTags] = useDeleteCustomerTagMutation();
  // Tag names seen so far, so selected tags can be shown as chips even when
  // the search hides them.
  const [tagNames, setTagNames] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (tags || []).map((tag) => [tag.tagId, tag.tags?.tagName || ""]),
    ),
  );

  useEffect(() => {
    if (!allTags?.length) return;
    setTagNames((current) => ({
      ...current,
      ...Object.fromEntries(allTags.map((tag) => [tag.id, tag.tagName])),
    }));
  }, [allTags]);

  const handleSave = async () => {
    try {
      const existingTagIds = new Set(tags?.map((tag) => tag.tagId) || []);

      const newTagIds = tagsValue.filter((tagId) => !existingTagIds.has(tagId));
      const removed = [...existingTagIds].some(
        (tagId) => !tagsValue.includes(tagId),
      );

      if (removed) {
        // Unticked tags are removed: save the client's whole tag list.
        await replaceTags({ tagIds: tagsValue, customerId }).unwrap();
        return;
      }

      if (!newTagIds.length) {
        return;
      }

      await addTags({
        tagIds: newTagIds,
        customerId,
      });
    } catch (e) {
    } finally {
      onClose();
      setTagValue("");
    }
  };

  return (
    <div className={"modal modal-open"}>
      <div className={"modal-box w-[440px] rounded-[10px] bg-white p-0"}>
        <div
          className={
            "mb-2 border-b border-gray-300 p-2 text-base font-semibold"
          }>
          Add Tag(s)
        </div>
        <div className={"px-2"}>
          <div
            className={
              "input input-bordered flex h-8 w-full items-center gap-2 pl-2"
            }>
            <SearchIcon size={20} className={"shrink-0"} />
            {tagsValue.length > 0 && (
              <FitChips
                items={tagsValue.map((tagId) => ({
                  key: tagId,
                  label: tagNames[tagId] || "…",
                }))}
                onRemove={(tagId) =>
                  setTagsValue((prev) => prev.filter((item) => item !== tagId))
                }
              />
            )}
            <input
              className={classNames(
                "flex items-center text-sm focus:outline-none",
                tagsValue.length ? "w-[90px] flex-none" : "w-full",
              )}
              placeholder={tagsValue.length ? "" : "Search tags..."}
              onChange={(e) => setTagValue(e.target.value)}
            />
          </div>
        </div>
        {isError && (
          <div className={"w-full text-sm font-bold text-error"}>
            This tag is already exist
          </div>
        )}
        <div
          className={
            "mt-2 flex h-[300px] w-full flex-wrap gap-0 overflow-y-auto border-t border-gray-200 pr-2"
          }>
          <div className={"mb-4 h-fit w-full"}>
            {allTags?.map((i) => (
              <div
                className={"flex w-[100%] items-center gap-2 px-2 py-2"}
                key={i.id}>
                <input
                  className={
                    "checkbox flex scale-75 items-center gap-2 border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
                  }
                  type={"checkbox"}
                  placeholder={"Search tags..."}
                  checked={tagsValue.some((item) => item === i.id)}
                  onChange={(e) =>
                    e.target.checked
                      ? setTagsValue((prev) => [...prev, i.id])
                      : setTagsValue((prev) =>
                          prev.filter((item) => item !== i.id),
                        )
                  }
                />
                <div className={"text-sm font-semibold"}>{i.tagName}</div>
              </div>
            ))}
          </div>
        </div>
        <div
          className={
            "mt-4 flex justify-center gap-3 px-2 pb-2 text-sm font-semibold"
          }>
          <button
            onClick={() => {
              onClose();
              setTagsValue([]);
              setIsError(false);
              setTagValue("");
            }}
            className={
              "w-full rounded-lg border border-[#C8D2DE] px-4 py-2 text-[#253747] disabled:opacity-75"
            }>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className={
              "w-full rounded-lg bg-[#7c66f0] px-4 py-2 text-white disabled:opacity-75"
            }>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTagsModal;
